// Coordinates the "filter refresh" loading barrier between the analytics side
// panel (filter options) and the main screen (KPI + tab data). See
// ./analyticsRefresh.ts for the pure state machine.
//
// The hook degrades gracefully when no provider is mounted (e.g. the emissions
// dashboard, which shares the side panel but has no barrier): cycleId stays 0,
// isFilterRefreshing stays false, and register/end become no-ops. So consumers
// keep their own array-driven refetch + local loaders and only *additionally*
// couple into the barrier when a provider is present (the analytics page).
import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import { useFilters } from './FiltersContext';
import { refreshReducer, initialRefreshState, selectIsFilterRefreshing } from './analyticsRefresh';

export interface AnalyticsRefreshContextType {
  cycleId: number;
  isFilterRefreshing: boolean;
  registerPanel: () => () => void; // returns an unregister cleanup
  registerMain: () => () => void;
  endPanel: (cycleId: number) => void;
  endMain: (cycleId: number) => void;
}

// Graceful default used when no provider is mounted.
const DEFAULT_VALUE: AnalyticsRefreshContextType = {
  cycleId: 0,
  isFilterRefreshing: false,
  registerPanel: () => () => {},
  registerMain: () => () => {},
  endPanel: () => {},
  endMain: () => {},
};

const AnalyticsRefreshContext = createContext<AnalyticsRefreshContextType | undefined>(undefined);

export const AnalyticsRefreshProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const filters = useFilters();
  const [state, dispatch] = useReducer(refreshReducer, initialRefreshState);

  const {
    licenseeData,
    subCompanyData,
    branchData,
    subCustomerData,
    countryOriginData,
    countryDestinationData,
    cityOriginData,
    cityDestinationData,
    transportModeData,
  } = filters;

  // Bump a cycle whenever a filter selection actually changes. Reference-compare
  // against the previous arrays (rather than a "first render" flag) so React
  // StrictMode's mount→cleanup→mount double-invoke does not emit a spurious cycle.
  const prevArraysRef = useRef<any[] | null>(null);
  useEffect(() => {
    const current = [
      licenseeData,
      subCompanyData,
      branchData,
      subCustomerData,
      countryOriginData,
      countryDestinationData,
      cityOriginData,
      cityDestinationData,
      transportModeData,
    ];
    if (prevArraysRef.current === null) {
      prevArraysRef.current = current;
      return;
    }
    const changed = current.some((v, i) => v !== prevArraysRef.current![i]);
    prevArraysRef.current = current;
    if (changed) dispatch({ type: 'startCycle' });
  }, [
    licenseeData,
    subCompanyData,
    branchData,
    subCustomerData,
    countryOriginData,
    countryDestinationData,
    cityOriginData,
    cityDestinationData,
    transportModeData,
  ]);

  const registerPanel = useCallback(() => {
    dispatch({ type: 'registerPanel' });
    return () => dispatch({ type: 'unregisterPanel' });
  }, []);
  const registerMain = useCallback(() => {
    dispatch({ type: 'registerMain' });
    return () => dispatch({ type: 'unregisterMain' });
  }, []);
  const endPanel = useCallback((cycleId: number) => dispatch({ type: 'endPanel', cycleId }), []);
  const endMain = useCallback((cycleId: number) => dispatch({ type: 'endMain', cycleId }), []);

  const value = useMemo<AnalyticsRefreshContextType>(
    () => ({
      cycleId: state.cycleId,
      isFilterRefreshing: selectIsFilterRefreshing(state),
      registerPanel,
      registerMain,
      endPanel,
      endMain,
    }),
    [state, registerPanel, registerMain, endPanel, endMain]
  );

  return <AnalyticsRefreshContext.Provider value={value}>{children}</AnalyticsRefreshContext.Provider>;
};

export const useAnalyticsRefresh = (): AnalyticsRefreshContextType =>
  useContext(AnalyticsRefreshContext) ?? DEFAULT_VALUE;
